/**
 * Landing-page FAQ content — the single source for both the rendered
 * accordion (via the i18n dict in ./i18n.tsx) and the FAQPage JSON-LD on
 * the two homepages (./HomeJsonLd.tsx). Keeping one source means the structured data can
 * never drift from what visitors actually see (a Google requirement for
 * FAQ rich results).
 *
 * Plain module on purpose: i18n.tsx is "use client", but the server-rendered
 * HomeJsonLd needs this data too — importing data exports across that boundary breaks,
 * importing a plain module from both sides doesn't.
 *
 * The landing page renders the first RENDERED_FAQ_COUNT entries; the rest
 * stay available for other surfaces.
 */

export interface FaqEntry {
  q: string;
  a: string;
}

/** How many entries the landing accordion shows — JSON-LD must match. */
export const RENDERED_FAQ_COUNT = 5;

export const FAQ_EN: FaqEntry[] = [
  {
    q: "Which properties does PlotScore cover?",
    a: "Every property in the Netherlands — residential, commercial, industrial, and mixed-use. Over 10 million parcels, from a single apartment in Groningen to a logistics hub in Rotterdam. If it has a BAG identifier, we can score it.",
  },
  {
    q: "How fresh is the data?",
    a: "Most sources refresh daily (Kadaster, BAG, WOZ). Energy labels from EP-Online sync weekly, and CBS demographic data is updated quarterly. Every report shows the exact timestamp of each underlying dataset, so you always know what you're looking at.",
  },
  {
    q: "How accurate is the score?",
    a: "The score is calibrated against national registry data — WOZ trajectories, CBS market statistics and the public registers behind our data-sources page (eight primary registers, 25+ feeds in total). Every factor is traceable — click any dimension and you see exactly which data points moved the score, with the source behind each one. No black box, no unexplained numbers.",
  },
  {
    q: "Is my data safe? AVG / GDPR compliant?",
    a: "Yes. All data is hosted in the EU (Amsterdam), and we only process public property data from authoritative Dutch sources — no personal information about current occupants is stored or displayed. Fully AVG/GDPR compliant, and we'll sign a processor agreement on request for institutional clients.",
  },
  {
    q: "What does it cost?",
    a: "Three scores are free with any account, no card required. After that: Buyer's Pass at €39 for 30 days unlimited (homebuyers), Pro at €49/month for brokers and advisors, Studio at €199/month for developers and investors. Enterprise plans for institutions start at €12,000/year. See the pricing page for the full breakdown.",
  },
  {
    q: "Can I export reports? Is there an API?",
    // Scoped against the implementation: PDF is ungated but watermarked below
    // Buyer's Pass (Sidebar.tsx), and CSV exports the saved-plot library — not a
    // score — behind a Pro check that 402s everyone else (api/account/export.csv).
    a: "Every score exports to PDF; below Buyer's Pass it carries a free-preview watermark. Pro and Studio add a CSV export of your saved-plot library. A REST API is on the roadmap for Q4 2026.",
  },
  {
    q: "Do you cover Belgium or Germany?",
    a: "The Netherlands comes first — the open-data ecosystem here is world-class and it's our home market. Belgium and Germany are on the roadmap for 2027, contingent on similar data access in those markets.",
  },
];

export const FAQ_NL: FaqEntry[] = [
  {
    q: "Welke panden dekt PlotScore?",
    a: "Elk pand in Nederland — wonen, bedrijfsmatig, industrieel en gemengd gebruik. Ruim 10 miljoen percelen, van een appartement in Groningen tot een logistiek knooppunt in Rotterdam. Als het een BAG-identificatie heeft, kunnen we het scoren.",
  },
  {
    q: "Hoe vers is de data?",
    a: "De meeste bronnen worden dagelijks ververst (Kadaster, BAG, WOZ). Energielabels van EP-Online synchroniseren wekelijks en demografische CBS-data wordt per kwartaal bijgewerkt. Elk rapport toont het exacte tijdstempel van elke onderliggende dataset, zodat je altijd weet waar je naar kijkt.",
  },
  {
    q: "Hoe nauwkeurig is de score?",
    a: "De score is gekalibreerd op nationale registerdata — WOZ-trajecten, CBS-marktstatistieken en de openbare registers achter onze databronnen-pagina (acht primaire registers, 25+ feeds in totaal). Elke factor is herleidbaar — klik op een dimensie en je ziet precies welke datapunten de score hebben beïnvloed, met de bron erachter. Geen black box, geen onverklaarde getallen.",
  },
  {
    q: "Is mijn data veilig? AVG-proof?",
    a: "Ja. Alle data wordt gehost in de EU (Amsterdam), en we verwerken uitsluitend openbare vastgoeddata uit gezaghebbende Nederlandse bronnen — geen persoonsgegevens van huidige bewoners worden opgeslagen of getoond. Volledig AVG/GDPR-conform, en we tekenen op verzoek een verwerkersovereenkomst voor institutionele klanten.",
  },
  {
    q: "Wat kost het?",
    a: "Drie scores zijn gratis bij elk account, geen kaart vereist. Daarna: Buyer's Pass voor €39 per 30 dagen onbeperkt (kopers), Pro voor €49/maand voor makelaars en adviseurs, Studio voor €199/maand voor ontwikkelaars en investeerders. Enterprise-plannen voor instellingen starten bij €12.000/jaar. Bekijk de prijzenpagina voor de volledige uitsplitsing.",
  },
  {
    q: "Kan ik rapporten exporteren? Is er een API?",
    a: "Elke score exporteert naar PDF; onder Buyer's Pass draagt die een gratis-preview-watermerk. Pro en Studio voegen een CSV-export van je opgeslagen percelen toe. Een REST API staat op de roadmap voor Q4 2026.",
  },
  {
    q: "Dekken jullie België of Duitsland?",
    a: "Nederland komt eerst — het open-data-ecosysteem hier is wereldklasse en het is onze thuismarkt. België en Duitsland staan op de roadmap voor 2027, mits vergelijkbare data-toegang in die markten.",
  },
];

/** Flatten a FAQ list into the flat `faq.qN`/`faq.aN` keys the i18n dict uses. */
export function faqDictEntries(faqs: FaqEntry[]): Record<string, string> {
  return Object.fromEntries(
    faqs.flatMap((f, i) => [
      [`faq.q${i + 1}`, f.q],
      [`faq.a${i + 1}`, f.a],
    ])
  );
}

/** Schema.org FAQPage node for the entries the landing page actually renders. */
export function faqPageJsonLd(faqs: FaqEntry[], url: string) {
  return {
    "@type": "FAQPage",
    "@id": `${url}#faq`,
    mainEntity: faqs.slice(0, RENDERED_FAQ_COUNT).map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  };
}
